import styled, { keyframes } from "styled-components";

import { media } from "../../../../../styles/media";

// Reveal the checklist when it expands from the minimized bar. The card already
// clips overflow, so growing max-height reads as a genuine "expanding" open.
const expandIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
    max-height: 72px;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 2500px;
  }
`;

// ------------------------------------------------------------- outer card
export const OnboardingCardStyles = styled.section`
  width: 100%;
  max-width: 960px;
  background: ${({ theme }) => theme.colors.semantic.container.white};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
  border-radius: ${({ theme }) => theme.radius[16]};
  overflow: hidden;
  transform-origin: top;
  animation: ${expandIn} 0.35s ease both;

  .ob-header {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[16]};
    padding: 22px 26px 16px;

    .ob-heading {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: ${({ theme }) => theme.spacing[12]};
    }

    .ob-title {
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.2px;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    .ob-desc {
      font-size: 13.5px;
      line-height: 19px;
      color: ${({ theme }) => theme.colors.primary[500]};
      margin-top: 3px;
    }

    .ob-header-right {
      display: flex;
      align-items: center;
      gap: ${({ theme }) => theme.spacing[16]};
    }

    .ob-minimize {
      border: 1px solid ${({ theme }) => theme.colors.primary[150]};
      background: ${({ theme }) => theme.colors.semantic.container.white};
      color: ${({ theme }) => theme.colors.primary[400]};
      cursor: pointer;
      width: 30px;
      height: 30px;
      border-radius: ${({ theme }) => theme.radius[8]};
      display: grid;
      place-items: center;
      flex-shrink: 0;

      &:hover {
        background: ${({ theme }) => theme.colors.accent[50]};
        color: ${({ theme }) => theme.colors.semantic.accent.default};
      }
    }
  }

  .ob-steps {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 20px 8px;
  }

  .ob-recommended {
    padding: 6px 26px 22px;
    border-top: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    margin-top: 8px;

    .ob-rec-heading {
      display: flex;
      align-items: center;
      gap: ${({ theme }) => theme.spacing[8]};
      margin: 16px 0 6px;
      flex-wrap: wrap;

      .ob-rec-title {
        font-size: 13px;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.semantic.text.strong};
      }

      .ob-rec-note {
        font-size: 12px;
        color: ${({ theme }) => theme.colors.primary[400]};
      }
    }
  }

  ${media.maxMobile`
    .ob-header {
      flex-direction: column;
      align-items: stretch;
      padding: 18px 16px 14px;

      .ob-header-right {
        justify-content: space-between;
      }
    }
    .ob-steps { padding: 0 10px 8px; }
    .ob-recommended { padding: 6px 16px 20px; }
  `}
`;

// ------------------------------------------------------------- sparkle badge
export const SparkleBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius[12]};
  background: ${({ theme }) => theme.colors.accent[50]};
  color: ${({ theme }) => theme.colors.semantic.accent.default};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

// ------------------------------------------------------------- progress bar
export const ProgressBarStyles = styled.div`
  min-width: 180px;

  .ob-progress-label {
    display: flex;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing[16]};
    margin-bottom: 6px;

    .ob-progress-caption {
      font-size: 12px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.primary[500]};
    }

    .ob-progress-count {
      font-size: 12px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
      white-space: nowrap;
    }
  }

  .ob-progress-track {
    height: 7px;
    border-radius: ${({ theme }) => theme.radius.rounded};
    background: ${({ theme }) => theme.colors.primary[100]};
    overflow: hidden;
  }

  .ob-progress-fill {
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ theme }) => theme.colors.semantic.accent.default};
    border-radius: ${({ theme }) => theme.radius.rounded};
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${media.maxMobile`
    min-width: 0;
    flex: 1;
  `}
`;

// ------------------------------------------------------------- step indicator
export const IndicatorStyles = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: all 0.25s ease;

  ${({ $status, theme }) => {
    switch ($status) {
      case "done":
        return `
          background: ${theme.colors.success[50]};
          border: 1px solid ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
        `;
      case "active":
      case "pending":
        return `
          background: ${theme.colors.semantic.accent.default};
          color: ${theme.colors.semantic.accent.onAccent};
          box-shadow: 0 4px 10px rgba(41, 35, 89, 0.22);
        `;
      default:
        return `
          background: ${theme.colors.accent[50]};
          border: 1px solid ${theme.colors.primary[100]};
          color: ${theme.colors.primary[300]};
        `;
    }
  }}
`;

// ------------------------------------------------------------- required row
export const StepRowStyles = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radius[12]};
  transition: background 0.25s ease, border-color 0.25s ease, padding 0.2s ease;
  opacity: ${({ $locked }) => ($locked ? 0.62 : 1)};

  ${({ $active, theme }) =>
    $active
      ? `
        background: ${theme.colors.accent[50]};
        border: 1px solid ${theme.colors.accent[100]};
        padding: 16px 18px;
      `
      : `
        background: transparent;
        border: 1px solid transparent;
        padding: 11px 18px;
      `}

  .ob-connector {
    position: absolute;
    left: 34px;
    top: 46px;
    bottom: -6px;
    width: 2px;
    border-radius: 2px;
    background: ${({ $done, theme }) =>
      $done ? theme.colors.success[100] : theme.colors.primary[100]};
  }

  .ob-step-row {
    display: flex;
    align-items: ${({ $active }) => ($active ? "flex-start" : "center")};
    gap: 14px;
  }

  .ob-indicator-wrap {
    position: relative;
    z-index: 1;
  }

  .ob-step-main {
    flex: 1;
    min-width: 0;
  }

  .ob-step-head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ob-step-num {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: ${({ $active, theme }) =>
      $active ? theme.colors.semantic.accent.default : theme.colors.primary[300]};
  }

  .ob-step-title {
    font-size: ${({ $active }) => ($active ? "15px" : "14px")};
    font-weight: 600;
    color: ${({ $locked, theme }) =>
      $locked ? theme.colors.primary[500] : theme.colors.semantic.text.strong};
  }

  .ob-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  .ob-badge-done {
    color: ${({ theme }) => theme.colors.success[600]};
  }

  .ob-badge-pending {
    gap: 5px;
    color: ${({ theme }) => theme.colors.warn[600]};
    background: ${({ theme }) => theme.colors.warn[50]};
    border: 1px solid ${({ theme }) => theme.colors.warn[100]};
    padding: 2px 8px;
    border-radius: ${({ theme }) => theme.radius.rounded};
  }

  .ob-step-desc {
    font-size: 13.5px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primary[500]};
    margin-top: 6px;
    max-width: 560px;
  }

  .ob-checks {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 12px;
  }

  .ob-check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.primary[500]};

    &.is-met {
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    .ob-check-mark {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: grid;
      place-items: center;
      background: ${({ theme }) => theme.colors.semantic.container.white};
      border: 1px solid ${({ theme }) => theme.colors.primary[150]};
      color: ${({ theme }) => theme.colors.primary[200]};
    }

    &.is-met .ob-check-mark {
      background: ${({ theme }) => theme.colors.success[50]};
      border-color: ${({ theme }) => theme.colors.success[100]};
      color: ${({ theme }) => theme.colors.success[600]};
    }
  }

  .ob-cta-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    flex-wrap: wrap;
  }

  .ob-cta-hint {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.primary[400]};
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ob-locked-hint {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.primary[400]};
    margin-top: 3px;
  }
`;

// ------------------------------------------------------------- recommended row
export const RecRowStyles = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 10px 4px;

  .ob-rec-main {
    flex: 1;
    min-width: 0;
  }

  .ob-rec-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .ob-rec-name {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
  }

  .ob-rec-added {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.success[600]};
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .ob-rec-optional {
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary[400]};
    background: ${({ theme }) => theme.colors.accent[50]};
    padding: 2px 8px;
    border-radius: ${({ theme }) => theme.radius.rounded};
  }

  .ob-rec-desc {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.primary[400]};
    margin-top: 2px;
  }
`;

// Shared inline-flex helper for CTA buttons (Button spreads `style`).
export const CTA_BUTTON_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "9px 15px",
  fontWeight: 600,
  fontSize: "13.5px"
};
